import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { getUserTypeDisplay, User, UserType } from '../../../../../../models/user';

import { AccountService } from '../../../../../../services/account/account.service';
import { UserService } from '../../../../../../services/user/user.service';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SimpleMessageModalComponent } from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { environment } from '../../../../../../../environments/environment';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { OrganizationConfig } from '../../../../../../models/organization-config';
import { OrgPOSSoftware } from '../../../../../../models/organization-config-enums';
import {
  SetUserLightspeedUserModalComponent
} from './modals/set-user-lightspeed-user-modal/set-user-lightspeed-user-modal.component';
import { CallbackResult } from '../../../../../../utils';


/**
 * The component representing a user: either an existing user to view/edit,
 * or a new user to add.
 */
@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css']
})
export class UserComponent implements OnInit, ComponentHasUnsavedChanges {

  public isNewUser: boolean = null;
  public user: User;
  public orgConfig: OrganizationConfig;

  public newPassword = '';
  public newPasswordConfirm = '';
  public newPin = '';
  public newPinConfirm = '';

  public apiToken: string = null;

  public userInfoErrorMessage = '';
  public userInfoErrors: any = {};
  public userInfoSuccessMessage = '';
  public setPasswordErrorMessage = '';
  public setPasswordErrors: any = {};
  public setPasswordSuccessMessage = '';
  public removePasswordErrorMessage = '';
  public removePasswordSuccessMessage = '';
  public resetPasswordEmailErrorMessage = '';
  public resetPasswordEmailSuccessMessage = '';
  public setPinErrorMessage = '';
  public setPinErrors: any = {};
  public setPinSuccessMessage = '';
  public removePinErrorMessage = '';
  public removePinSuccessMessage = '';
  public disableTwoFactorAuthErrorMessage = '';
  public getAPITokenErrorMessage = '';

  public loadingUser = false;
  public loadingSubmitUserInfo = false;
  public loadingSetPassword = false;
  public loadingRemovePassword = false;
  public loadingSendResetPasswordEmail = false;
  public loadingSetPin = false;
  public loadingRemovePin = false;
  public loadingDisableTwoFactorAuth = false;
  public loadingAPIToken = false;

  public userNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public readonly UserType = UserType;
  public readonly OrgPOSSoftware = OrgPOSSoftware;
  public readonly apiUrl = environment.api_url;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private userService: UserService,
    private orgConfigService: OrganizationConfigService,
    private modalService: NgbModal,
  ) { }

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewUser = !routeParams.hasOwnProperty('id');

    this.orgConfig = this.orgConfigService.getOrganizationConfig();
    if (this.orgConfig === null) {
      this.orgConfigService.loadOrganizationConfig().then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();
      });
    }

    // Either get the user to edit (if a user ID is provided) or construct
    // an empty user for creation.
    this.resetUser();
    if (!this.isNewUser) {
      this.loadUser(routeParams.id);
    }
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the user with the given ID (only used for editing users).
   * @param userId - The API ID of the user.
   */
  public loadUser(userId: string) {
    this.loadingUser = true;

    const userIdInt = parseInt(userId, 10);
    if (isNaN(userIdInt)) {
      this.userNotFound = true;
      this.loadingUser = false;
      return;
    }

    this.userService
      .getUser(userIdInt)
      .then(user => {
        this.user = user;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.userNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingUser = false; });
  }

  /**
   * Handle a user form submission.
   */
  public onUserInfoSubmit(continueEditing: boolean): Promise<void> {
    this.loadingSubmitUserInfo = true;

    this.clearFeedback();

    // Success handling is different between creating and updating.
    let userSubmitPromise: Promise<void>;
    if (this.isNewUser) {

      userSubmitPromise = this.userService
        .addUser(this.user)
        .then(createdUser => {
          this.unsavedChanges = false;

          // Display success notification.
          this.userInfoSuccessMessage = createdUser.first_name + ' ' + createdUser.last_name + ' added successfully.';

          this.user = createdUser;
          this.isNewUser = false;

          this.router.navigate(
            [
              '/users',
              this.user.id.toString()
            ],
            {}
          );
        });

    } else {

      userSubmitPromise = this.userService
        .updateUser(this.user)
        .then(updatedUser => {
          this.unsavedChanges = false;

          const successMessage = updatedUser.first_name + ' ' + updatedUser.last_name + ' saved successfully.';

          if (continueEditing) {
            this.userInfoSuccessMessage = successMessage;
          } else {
            this.router.navigate(
              ['/users'],
              {
                state: {
                  feedback: {
                    text: successMessage,
                    status: 'success'
                  }
                }
              }
            );
          }
        });

    }

    // Error handling is the same for both creating and updating.
    return userSubmitPromise
      .catch(reason => {
        if (reason.status === 400) {
          this.userInfoErrors = reason.error;
          this.userInfoErrorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (this.isNewUser) {
            this.userInfoErrorMessage = 'You do not have permission to add users.';
          } else {
            this.userInfoErrorMessage = 'You do not have permission to modify this user.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmitUserInfo = false; });
  }

  /**
   * Set the entered user information back to the default values.
   */
  public resetUser() {
    this.user = {
      id: null,
      url: '',
      email: '',
      first_name: '',
      last_name: '',
      user_type: UserType.staffMember,
      default_gym_id: this.accountService.getMyAccount().default_gym_id,
      default_gym_name: '',
      default_gym_url: '',
      date_joined: '',
      is_active: true,
      can_log_in_to_staff_site: false,
      can_manage_events: true,
      can_manage_members: true,
      can_manage_customer_proficiencies: true,
      can_export_customers: false,
      can_access_reports: false,
      has_griptonite_access: false,
      can_look_up_members_by_barcode: false,
      is_organization_admin: false,
      pin_mode_required: false,
      otp_enabled: false,
      lightspeed_user_id: '',
      lightspeed_user_username: '',
      lightspeed_user_display_name: '',
      has_password: false,
      has_pin: false
    };
  }

  /**
   * Whether the page is currently waiting for any API responses.
   */
  public isLoading() {
    return (
      this.loadingUser ||
      this.loadingSubmitUserInfo ||
      this.loadingSetPassword ||
      this.loadingRemovePassword ||
      this.loadingSendResetPasswordEmail ||
      this.loadingSetPin ||
      this.loadingRemovePin
    );
  }

  /**
   * Update the user's password according to the provided values.
   */
  public setPassword() {
    this.loadingSetPassword = true;

    this.clearFeedback();

    // Check for field errors, namely missing required fields.
    if (!this.newPassword) {
      this.setPasswordErrors.new_password = 'This field is required.';
    }
    if (!this.newPasswordConfirm) {
      this.setPasswordErrors.new_password_confirm = 'This field is required.';
    }

    if (Object.entries(this.setPasswordErrors).length !== 0) {
      this.setPasswordErrorMessage = 'Please correct the errors above and try again.';
      this.loadingSetPassword = false;
      return;
    }

    // Check that the new password confirmation matches (this is not done by the API).
    if (this.newPassword !== this.newPasswordConfirm) {
      this.setPasswordErrorMessage = 'New passwords do not match.';
      this.loadingSetPassword = false;
      return;
    }

    this.userService
      .changeUserPassword(this.user, this.newPassword)
      .then(() => {
        this.setPasswordSuccessMessage = 'Password set successfully.';

        // Clear password input fields
        this.newPassword = '';
        this.newPasswordConfirm = '';

        // Update user has_password status
        this.user.has_password = true;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.setPasswordErrorMessage = reason.error.password;
        } else if (reason.status === 403) {
          this.setPasswordErrorMessage = 'You do not have permission ' +
                                            'to set this user\'s password.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSetPassword = false; });
  }

  public removePassword() {
    this.loadingRemovePassword = true;

    this.clearFeedback();

    this.userService
      .removeUserPassword(this.user)
      .then(() => {
        this.removePasswordSuccessMessage = 'Password removed successfully.';

        // Update user has_password status
        this.user.has_password = false;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.removePasswordErrorMessage = 'You do not have permission ' +
                                            'to remove this user\'s password.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRemovePassword = false; });
  }

  /**
   * Reset the password input fields and feedback messages.
   */
  public resetSetPasswordInput() {
    this.newPassword = '';
    this.newPasswordConfirm = '';

    this.clearFeedback();
  }

  /**
   * Update the user's pin according to the provided values.
   */
  public setPin() {
    this.loadingSetPin = true;

    this.clearFeedback();

    // Check for field errors, namely missing required fields.
    if (!this.newPin) {
      this.setPinErrors.new_pin = 'This field is required.';
    }
    if (!this.newPinConfirm) {
      this.setPinErrors.new_pin_confirm = 'This field is required.';
    }

    if (Object.entries(this.setPinErrors).length !== 0) {
      this.setPinErrorMessage = 'Please correct the errors above and try again.';
      this.loadingSetPin = false;
      return;
    }

    // Check that the new pin confirmation matches (this is not done by the API).
    if (this.newPin !== this.newPinConfirm) {
      this.setPinErrorMessage = 'New pins do not match.';
      this.loadingSetPin = false;
      return;
    }

    this.userService
      .changeUserPin(this.user, this.newPin)
      .then(() => {
        this.setPinSuccessMessage = 'Pin set successfully.';

        // Clear pin input fields
        this.newPin = '';
        this.newPinConfirm = '';

        // Update user has_pin status
        this.user.has_pin = true;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.setPinErrorMessage = reason.error.pin;
        } else if (reason.status === 403) {
          this.setPinErrorMessage = 'You do not have permission ' +
                                            'to set this user\'s pin.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSetPin = false; });
  }

  public removePin() {
    this.loadingRemovePin = true;

    this.clearFeedback();

    this.userService
      .removeUserPin(this.user)
      .then(() => {
        this.removePinSuccessMessage = 'Pin removed successfully.';

        // Update user has_pin status
        this.user.has_pin = false;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.removePinErrorMessage = 'You do not have permission ' +
                                            'to remove this user\'s pin.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRemovePin = false; });
  }

  /**
   * Reset the pin input fields and feedback messages.
   */
  public resetSetPinInput() {
    this.newPin = '';
    this.newPinConfirm = '';

    this.clearFeedback();
  }

  public getSetPasswordEmailTooltip(): string {
    if (this.user.email === '') {
      return 'The user must have an email address in order to set a password.';
    }
    if (!this.user.can_log_in_to_staff_site) {
      return 'The user must be able to log in to the Staff Site in order to ' +
        'set a password. Change their "Can Log In To Staff Site" permission ' +
        'in order to proceed.';
    }
    return '';
  }

  public handlePasswordResetEmailSubmit() {
    this.clearFeedback();

    if (this.unsavedChanges) {
      this.onUserInfoSubmit(true)
        .then(() => {
          if (this.userInfoSuccessMessage !== '') {
            this.sendPasswordResetEmail();
          }
        })
    } else {
      this.sendPasswordResetEmail();
    }
  }

  public openDisableTwoFactorAuthModal() {
    this.clearFeedback();

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Disable Two-Factor Authentication';
    modalRef.componentInstance.messageText = 'Are you sure you want to disable ' +
      'two-factor authentication for this user? An authenticator code will no longer be ' +
      'required when they log into the Staff Site.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDisableTwoFactorAuth = true;

      this.userService
        .removeUserOTP(this.user)
        .then(() => {
          this.loadUser(this.user.id.toString());
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.disableTwoFactorAuthErrorMessage = reason.error;
          } else if (reason.status === 403) {
            this.disableTwoFactorAuthErrorMessage = 'You do not have permission ' +
              'to disable two-factor authentication.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDisableTwoFactorAuth = false; });
    }
  }

  public clearNameAndEmail() {
    this.user.first_name = '';
    this.user.last_name = '';
    this.user.email = '';
  }

  public loadAPIToken() {
    if (this.loadingAPIToken) { return; }

    this.loadingAPIToken = true;

    this.userService
      .getAPIIntegrationToken(this.user)
      .then(token => { this.apiToken = token; })
      .catch(reason => {
        if (reason.status === 403) {
          this.getAPITokenErrorMessage =
            'You do not have permission to obtain this API token.';
        } else if (reason.status === 400) {
          this.getAPITokenErrorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAPIToken = false; });
  }

  public openSetLightspeedUserModal() {
    const modalRef = this.modalService.open(SetUserLightspeedUserModalComponent);
    modalRef.componentInstance.user = this.user;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.loadUser(this.user.id.toString());
    }
  }

  private sendPasswordResetEmail() {
    this.loadingSendResetPasswordEmail = true;

    this.userService
      .sendPasswordResetEmail(this.user.email)
      .then(() => {
        this.resetPasswordEmailSuccessMessage = 'Password reset email sent successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.resetPasswordEmailErrorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSendResetPasswordEmail = false; });
  }

  private clearFeedback() {
    this.userInfoErrorMessage = '';
    this.userInfoErrors = {};
    this.userInfoSuccessMessage = '';
    this.setPasswordErrorMessage = '';
    this.setPasswordErrors = {};
    this.setPasswordSuccessMessage = '';
    this.removePasswordErrorMessage = '';
    this.removePasswordSuccessMessage = '';
    this.resetPasswordEmailErrorMessage = '';
    this.resetPasswordEmailSuccessMessage = '';
    this.setPinErrorMessage = '';
    this.setPinErrors = {};
    this.setPinSuccessMessage = '';
    this.removePinErrorMessage = '';
    this.removePinSuccessMessage = '';
  }

}
